// Translation
import { Trans, useTrans } from "@/i18n"

// UI
import { Disclaimer } from "@/components/Disclaimer"
import { CardWrapper } from "@/components/Card"

// Specific UI
import { StepCompletedOverlay } from "../StepCompletedOverlay"
import {
	MangopayGate,
	useMangopayGateActive,
} from "@/pages/investor/pages/_components/MangopayGate"

// Context
import { useCurrentUserIdentityType, useCurrentUserKyc } from "@/context/user"

// GraphQL
import {
	RiskKycStateChoices,
	useUserKycStatusQuery,
	KycVerificationDocumentType,
	UsersUserProfileIdentityTypeChoices,
} from "@/api/graphql"

// Env variables
import {
	EXTERNAL_PLATFORM_AFM_LICENSE_URL,
	EXTERNAL_PLATFORM_MANGOPAY_SHAREHOLDER_DECLARATION_URL,
	EXTERNAL_PLATFORM_KVK_LEI_URL,
	EXTERNAL_PLATFORM_ZONHUB_FAQ_URL,
} from "@/lib/env"

// Constants
import { contact } from "@/constants/constants"

/**
 * BusinessVerificationForm — manual business verification instructions.
 * Only renders for business accounts on the legacy (non-hosted) KYC flow;
 * the hosted flow has its own banner, and private users use a different form.
 */
export function BusinessVerificationForm() {
	const userKyc = useCurrentUserKyc()
	const usesHostedKyc = userKyc?.uses_hosted_verification ?? false
	const identityType = useCurrentUserIdentityType()
	const isBusiness =
		identityType === UsersUserProfileIdentityTypeChoices.Business
	if (usesHostedKyc || !isBusiness) {
		return null
	}
	return <BusinessVerificationFormContent />
}

function BusinessVerificationFormContent() {
	const t = useTrans("investor")

	const { data } = useUserKycStatusQuery()
	const gateActive = useMangopayGateActive()

	return (
		<MangopayGate>
			<CardWrapper className="relative">
				{/** Show completed overlay when status is successfull.
				    Suppressed while the Mangopay gate is active so its
				    overlay doesn't double up with ours. */}
				{!gateActive &&
				data?.me?.kyc?.state ===
					RiskKycStateChoices.VerificationSuccess ? (
					<StepCompletedOverlay
						state={data?.me?.kyc?.state}
						last_verified_on={data?.me?.kyc?.last_verified_on}
						documents={
							data?.me?.kyc?.documents?.filter(
								Boolean,
							) as KycVerificationDocumentType[]
						}
					/>
				) : null}

				<Disclaimer
					className="mb-3"
					title={t("investor:identity.business.title")}
					message={
						<div className="w-full">
							<p className="mb-4">
								<Trans i18nKey="investor:identity.business.intro" />
							</p>
							<p className="mb-4">
								<Trans
									i18nKey="investor:identity.business.legal"
									components={{
										a: (
											<a // oxlint-disable-line jsx-a11y/anchor-has-content
												className="text-secondary-700 font-medium"
												href={
													EXTERNAL_PLATFORM_AFM_LICENSE_URL
												}
												target="_blank"
												rel="noreferrer"
											/>
										),
									}}
								/>
							</p>
							<p className="mb-4">
								<Trans
									i18nKey="investor:identity.business.requirements.intro"
									components={{
										a: (
											<a // oxlint-disable-line jsx-a11y/anchor-has-content
												className="text-secondary-700 font-medium"
												href={`mailto:${contact.emailAddress}`}
											/>
										),
									}}
								/>
							</p>
							<ol className="ml-0 list-decimal">
								<li>
									<Trans i18nKey="investor:identity.business.requirements.list.identity" />
								</li>
								<li>
									<Trans i18nKey="investor:identity.business.requirements.list.coc" />
								</li>
								<li>
									<Trans
										i18nKey="investor:identity.business.requirements.list.ubo"
										components={{
											a: (
												<a // oxlint-disable-line jsx-a11y/anchor-has-content
													className="text-secondary-700 font-medium"
													href={
														EXTERNAL_PLATFORM_MANGOPAY_SHAREHOLDER_DECLARATION_URL
													}
													target="_blank"
													rel="noreferrer"
												/>
											),
										}}
									/>
								</li>
							</ol>
							<p className="mb-4">
								<Trans i18nKey="investor:identity.business.requirements.extra" />
							</p>
							<ol className="mb-4 ml-0 list-decimal" start={4}>
								<li>
									<Trans i18nKey="investor:identity.business.requirements.list.articles" />
								</li>
								<li>
									<Trans
										i18nKey="investor:identity.business.requirements.list.lei"
										components={{
											a: (
												<a // oxlint-disable-line jsx-a11y/anchor-has-content
													className="text-secondary-700 font-medium"
													href={
														EXTERNAL_PLATFORM_KVK_LEI_URL
													}
													target="_blank"
													rel="noreferrer"
												/>
											),
										}}
									/>
								</li>
							</ol>
							<p className="mb-4">
								<Trans i18nKey="investor:identity.business.note" />
							</p>
							<p className="mb-4">
								<Trans i18nKey="investor:identity.business.contact" />
							</p>
							<p className="mb-4">
								<Trans
									i18nKey="investor:identity.business.help"
									components={{
										mail: (
											<a // oxlint-disable-line jsx-a11y/anchor-has-content
												className="text-secondary-700 font-medium"
												href={`mailto:${contact.emailAddress}`}
												target="_blank"
												rel="noreferrer"
											/>
										),
										faq: (
											<a // oxlint-disable-line jsx-a11y/anchor-has-content
												className="text-secondary-700 font-medium"
												href={
													EXTERNAL_PLATFORM_ZONHUB_FAQ_URL
												}
												target="_blank"
												rel="noreferrer"
											/>
										),
									}}
								/>
							</p>
						</div>
					}
				/>
			</CardWrapper>
		</MangopayGate>
	)
}
